import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Adult, Bags, CarName, GreenTick, Window } from '../../../assests/ExportImageIcon'
// CarCompany, StarWhite were only used by the old rating badge/brand row, removed to match car-detail.jsx's current card
// Cancellation, Delay, Lost were only used by the static TRIP_SECURE_BENEFITS mock (commented out below)
// Icici was only used by the commented-out ICICI Lombard logo above
import '../carListing/carResults.css'
import '../car-details/carDetail.css'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import { showToast } from '../../../componants/toast/toastService'
import { setCarBookingContext, setCarInsurancePlans, setSelectedInsurance } from '../../../redux/carRentalSearch/carRentalSearchSlice'
import { formatMoney } from '../../../utils/formatMoney'
import CarSearchLoader from '../../../componants/carSearchLoader/CarSearchLoader'
import SearchableSelect from '../../../componants/searchableSelect/SearchableSelect'
import DatePicker from '../../../componants/datePicker/DatePicker'

const CONTACT_DEFAULT = { email: '', mobile: '' }

const PASSENGER_DEFAULT = {
    firstName: '',
    lastName: '',
    mobile: '',
    dob: '',
    gender: '',
    drivingLicense: '',
    passportNumber: '',
    passportIssued: '',
    passportExpiry: '',
    // Sent as multipart file fields (passport_image/driving_licence_image)
    // alongside pax_details — see handleContinueToPayment/carPayment.jsx.
    drivingLicenseImage: null,
    passportImage: null,
}

const GENDER_OPTIONS = [
    { value: 'M', label: 'Male' },
    { value: 'F', label: 'Female' },
]

// Car rental is adult-only (18+) — the driver's DOB is capped so the
// calendar can't select anything younger than 18 years old today.
const MIN_DRIVER_AGE = 18
const maxDriverDob = () => {
    const today = new Date()
    const d = new Date(today.getFullYear() - MIN_DRIVER_AGE, today.getMonth(), today.getDate())
    return d.toISOString().split('T')[0]
}

const ageFromDob = (dob) => {
    if (!dob) return null
    const birth = new Date(dob)
    if (Number.isNaN(birth.getTime())) return null
    const today = new Date()
    let age = today.getFullYear() - birth.getFullYear()
    const monthDiff = today.getMonth() - birth.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--
    return age
}

const FieldError = ({ children }) => (
    children ? <p className='hotelGuestFieldError'>{children}</p> : null
)

const FileUploadField = ({ file, onChange }) => {
    const inputRef = React.useRef(null)
    return (
        <div className='fileUploadField'>
            <input
                ref={inputRef}
                type='file'
                accept='image/*,.pdf'
                hidden
                onChange={(e) => onChange(e.target.files?.[0] || null)}
            />
            <button type='button' className='commonBtnTransparent fileUploadField__btn' onClick={() => inputRef.current?.click()}>
                {file ? 'Change File' : 'Upload File'}
            </button>
            {file && (
                <span className='fileUploadField__name'>
                    {file.name}
                    <button type='button' className='fileUploadField__remove' onClick={() => onChange(null)} aria-label='Remove file'>×</button>
                </span>
            )}
        </div>
    )
}

// Renter Details section is commented out for now — these are only used by that commented-out block.
// const RENTER_DEFAULT = { drivingLicense: 'GHG12414124' }
// const RENTER_DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'))
// const RENTER_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
// const RENTER_YEARS = Array.from({ length: 80 }, (_, i) => String(2026 - i))
// const RENTER_COUNTRIES = ['India', 'United States', 'United Kingdom', 'United Arab Emirates', 'Other']

// Card fields moved to carPayment.jsx (the next page in the flow).

// Not returned by /cars/insurance — real plan benefits now render from the API response instead.
// const TRIP_SECURE_BENEFITS = [
//     { icon: Lost, title: 'Lost Baggage Assistance', subtext: '24x7 Support' },
//     { icon: Cancellation, title: 'Common Carrier Cancellation', subtext: '₹999 upto discount' },
//     { icon: Delay, title: 'Common Carrier Delay', subtext: '₹499 Flat Off' },
// ]

const TRAVELLER_INFO = [
    'Ensure passenger names match government-issued ID',
    'Name changes are not allowed after booking',
    'Valid ID proof required at check-in',
]

// No coupon/discount endpoint exists for cars — this was static placeholder data.
// const COUPONS = [
//     { code: 'GIRUSH', off: '₹ 312 off', desc: 'Use this coupon and get ₹ 312 instant discount on your flight booking.' },
//     { code: 'GIRUSH', off: '₹ 312 off', desc: 'Use this coupon and get ₹ 312 instant discount on your flight booking.' },
//     { code: 'GIRUSH', off: '₹ 312 off', desc: 'Use this coupon and get ₹ 312 instant discount on your flight booking.' },
//     { code: 'GIRUSH', off: '₹ 312 off', desc: 'Use this coupon and get ₹ 312 instant discount on your flight booking.' },
// ]

const IMPORTANT_INFO = [
    'Check-in begins 2 hours before departure',
    'Carry a valid government ID proof',
    'Web check-in available 48 hours before departure',
    "Baggage subject to airline's baggage policy",
]

const CarBookingDetail = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const bookingContext = useSelector((state) => state.carRentalSearch.bookingContext)
    const selectedCar = useSelector((state) => state.carRentalSearch.selectedCar)
    const details = useSelector((state) => state.carRentalSearch.details)
    const insurancePlans = useSelector((state) => state.carRentalSearch.insurancePlans)
    const selectedInsurance = useSelector((state) => state.carRentalSearch.selectedInsurance)

    const [travellerOpen, setTravellerOpen] = useState(true)
    // const [renterOpen, setRenterOpen] = useState(true) // only used by the commented-out Renter Details section
    // const [planeOpen, setPlaneOpen] = useState(true) // only used by the commented-out flight number section
    const [coverageOpen, setCoverageOpen] = useState(true)
    // const [tripSecureChoice, setTripSecureChoice] = useState('yes') // only used by the commented-out static Trip Secure widget
    const [insuranceLoading, setInsuranceLoading] = useState(false)
    const [insuranceLoaded, setInsuranceLoaded] = useState(false)

    const [title, setTitle] = useState('Mr')
    const [contact, setContact] = useState(CONTACT_DEFAULT)
    const [passenger, setPassenger] = useState(PASSENGER_DEFAULT)
    const [areaCode, setAreaCode] = useState('91')
    const [address, setAddress] = useState('')
    const [city, setCity] = useState('')
    const [state, setState] = useState('')
    const [country, setCountry] = useState('India')
    const [pincode, setPincode] = useState('')
    // Errors only render after a submit attempt, so the form doesn't show red
    // fields on first load.
    const [submitAttempted, setSubmitAttempted] = useState(false)
    const [noOfPassenger, setNoOfPassenger] = useState('1')
    const [flightNumber] = useState('') // setter only used by the commented-out flight number input
    // const [renter] = useState(RENTER_DEFAULT) // only used by the commented-out Renter Details section

    // Same car detail card as car-detail.jsx: prefer the /cars/details response
    // (apiCarDetails), fall back to the search result (selectedCar.car_details).
    const apiCarDetails = details?.car_details
    const CAR_INFO = {
        title: apiCarDetails?.name || selectedCar?.car_details?.car_name || 'Car',
        code: apiCarDetails?.code || selectedCar?.car_details?.car_code || '',
        transmission: apiCarDetails?.transmission || selectedCar?.car_details?.transmission_type || '',
        adults: apiCarDetails?.passengers ?? selectedCar?.car_details?.passenger_quantity ?? '-',
        bags: apiCarDetails?.baggage ?? selectedCar?.car_details?.baggage_quantity ?? '-',
        windows: selectedCar?.car_details?.vehicle_door ?? '-',
        image: apiCarDetails?.image_url || selectedCar?.car_details?.car_image_hd || selectedCar?.car_details?.car_image || CarName,
    }

    const loadInsurance = () => {
        if (!bookingContext || !passenger.firstName || !passenger.lastName) return
        setInsuranceLoading(true)
        service
            .post(endpoints.cars.insurance, {
                session_id: bookingContext.session_id,
                reference_id: bookingContext.reference_id,
                first_name: passenger.firstName,
                last_name: passenger.lastName,
            }, { timeout: 45000 })
            .then(({ data }) => {
                const plans = data.data?.data || []
                dispatch(setCarInsurancePlans(plans))
                // Trip Secure comes added by default — user can remove it with the Cancel button.
                if (plans.length) {
                    dispatch(setSelectedInsurance(plans[0]))
                }
                setInsuranceLoaded(true)
            })
            .catch(() => {
                dispatch(setCarInsurancePlans([]))
            })
            .finally(() => setInsuranceLoading(false))
    }

    // Auto-load Trip Secure insurance as soon as the passenger's name is available —
    // /cars/insurance requires first_name/last_name, so this fires the moment both
    // are filled instead of waiting for a manual "Load Insurance Details" click.
    useEffect(() => {
        if (!bookingContext || insuranceLoading || insuranceLoaded) return
        if (!passenger.firstName || !passenger.lastName) return
        loadInsurance()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingContext, passenger.firstName, passenger.lastName, insuranceLoaded])

    if (!bookingContext) {
        return (
            <section className='flightBookingDetails commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <p>No car selected. Please go back and search again.</p>
                    <button type='button' className='commonBtn' onClick={() => navigate('/car-list')}>Search Cars</button>
                </div>
            </section>
        )
    }

    // TOTAL AMOUNT = fare total (vehicle + coverage + extras) + selected insurance,
    // since insurance is added on this page and isn't included in total_label.
    const totalToPay = (Number(bookingContext.fare_summary?.total_fare) || 0) + (Number(selectedInsurance?.plan_cost_amount) || 0)
    const totalToPayLabel = formatMoney(totalToPay, bookingContext.currency)

    const handleContinueToPayment = () => {
        setSubmitAttempted(true)
        if (!passenger.firstName || !passenger.lastName) {
            showToast('Please enter passenger first and last name', 'error')
            return
        }
        if (!passenger.gender) {
            showToast('Please select gender', 'error')
            return
        }
        const driverAge = ageFromDob(passenger.dob)
        if (driverAge === null || driverAge < MIN_DRIVER_AGE) {
            showToast('Driver must be at least 18 years old to book a car', 'error')
            return
        }
        if (!passenger.drivingLicense && !passenger.passportNumber) {
            showToast('Please enter a driving license number or passport number', 'error')
            return
        }
        if (!contact.email || !contact.mobile) {
            showToast('Please enter contact email and mobile number', 'error')
            return
        }
        if (!address || !city || !state || !country || !pincode) {
            showToast('Please complete the renter address details', 'error')
            return
        }

        const [airlineCode, ...airlineNumberParts] = flightNumber.trim().split(' ')

        // Payment card details are collected on the next page (carPayment.jsx),
        // which calls /cars/book once the card fields are filled in.
        const nextBookingContext = {
            ...bookingContext,
            no_of_passenger: noOfPassenger,
            pax_details: {
                title,
                first_name: passenger.firstName,
                last_name: passenger.lastName,
                email_id: contact.email,
                area_code: areaCode,
                phone: contact.mobile,
                address,
                city,
                state,
                country,
                pincode,
                gender: passenger.gender,
                dob: passenger.dob,
                passport_no: passenger.passportNumber,
                driving_licence_no: passenger.drivingLicense,
            },
            // airline_details: {
            //     airline_code: airlineCode || '',
            //     airline_number: airlineNumberParts.join(' ') || '',
            // },
        }

        // Only pass insurance_plan_id when a plan is actually selected — sending
        // an empty string was tripping up /cars/book on staging.
        if (selectedInsurance?.plan_id) {
            nextBookingContext.insurance_plan_id = selectedInsurance.plan_id
        } else {
            delete nextBookingContext.insurance_plan_id
        }

        dispatch(setCarBookingContext(nextBookingContext))
        // File objects can't go through Redux (not serializable) — carried via
        // router state instead, read back in carPayment.jsx for the multipart
        // /cars/book submission (passport_image / driving_licence_image).
        navigate('/car-payment', {
            state: {
                drivingLicenseImage: passenger.drivingLicenseImage,
                passportImage: passenger.passportImage,
            },
        })
    }

    const driverAge = ageFromDob(passenger.dob)
    const licenseOrPassportMissing = !passenger.drivingLicense.trim() && !passenger.passportNumber.trim()
    const errors = {
        email: submitAttempted && !contact.email.trim(),
        mobile: submitAttempted && !contact.mobile.trim(),
        firstName: submitAttempted && !passenger.firstName.trim(),
        lastName: submitAttempted && !passenger.lastName.trim(),
        gender: submitAttempted && !passenger.gender,
        dob: submitAttempted && (driverAge === null || driverAge < MIN_DRIVER_AGE),
        drivingLicense: submitAttempted && licenseOrPassportMissing,
        passportNumber: submitAttempted && licenseOrPassportMissing,
        address: submitAttempted && !address.trim(),
        city: submitAttempted && !city.trim(),
        state: submitAttempted && !state.trim(),
        country: submitAttempted && !country.trim(),
        pincode: submitAttempted && !pincode.trim(),
    }

    return (
        <>
            <section className='flightBookingDetails commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <div className='row g-4'>
                        <div className='col-md-12'>
                            <div className='allBookingDetail'>
                                <div className='allBokkingInfo'>
                                    <div className='carResultCard bgWhite commonBorder borderRadius20'>
                                        <img src={CAR_INFO.image} alt={CAR_INFO.title} className='carResultCard__image' />

                                        <div className='carResultCard__title mt-0'>{CAR_INFO.title}{CAR_INFO.code ? ` (${CAR_INFO.code})` : ''}</div>
                                        <div className='carResultCard__subtitle'>{CAR_INFO.transmission}</div>

                                        <div className='carResultCard__specs'>
                                            <span><img src={Adult} alt="" /> {CAR_INFO.adults} Adults</span>
                                            <span><img src={Bags} alt="" /> {CAR_INFO.bags} Bags</span>
                                            <span><img src={Window} alt="" /> {CAR_INFO.windows} Doors</span>
                                        </div>

                                        <div className='bsFareDivider mt-4' />

                                        <div className='bsFareRow passengercount'>
                                            <span>No. of Passengers:</span>
                                            <input
                                                type='number'
                                                min='1'
                                                max='9'
                                                value={noOfPassenger}
                                                onChange={(e) => setNoOfPassenger(e.target.value)}
                                                style={{ width: 60, textAlign: 'right' }}
                                            />
                                        </div>
                                    </div>

                                    <div className='travellerDetail'>
                                        <div className='fareDetailsHead' onClick={() => setTravellerOpen(!travellerOpen)}>
                                            <div className='bsSectionTitle'>Traveller Details</div>
                                            <span className={`fareCollapseIcon ${travellerOpen ? 'open' : ''}`}>&#8963;</span>
                                        </div>

                                        {travellerOpen && (
                                            <div className='fareDetailsBody'>
                                                <div className='bsSectionTitle'>Contact Details</div>
                                                <div className='bsBox'>
                                                    <div className='travellerFieldRow'>
                                                        <div className='travellerField'>
                                                            <label>Email Address *</label>
                                                            <input
                                                                type='email'
                                                                required
                                                                className={errors.email ? 'travellerField__input--error' : ''}
                                                                value={contact.email}
                                                                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                                                            />
                                                            {errors.email && <FieldError>Email is required</FieldError>}
                                                        </div>
                                                        <div className='travellerField'>
                                                            <label>Area Code</label>
                                                            <input
                                                                type='text'
                                                                inputMode='numeric'
                                                                maxLength={4}
                                                                value={areaCode}
                                                                onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                                            />
                                                        </div>
                                                        <div className='travellerField'>
                                                            <label>Mobile Number *</label>
                                                            <input
                                                                type='tel'
                                                                inputMode='numeric'
                                                                maxLength={10}
                                                                required
                                                                className={errors.mobile ? 'travellerField__input--error' : ''}
                                                                value={contact.mobile}
                                                                onChange={(e) => setContact({ ...contact, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                                                            />
                                                            {errors.mobile && <FieldError>Mobile number is required</FieldError>}
                                                        </div>
                                                    </div>
                                                    <p className='travellerFieldNote'>Booking confirmation will be sent here</p>
                                                </div>

                                                <div className='bsSectionTitle mt-3'>Passenger Details</div>
                                                <div className='bsBox'>
                                                    <div className='travellerPassengerHead'>
                                                        <span>Passenger 1</span>
                                                        <span className='travellerPassengerType'>Adult</span>
                                                    </div>
                                                    <div className='travellerFieldRow'>
                                                        <div className='travellerField'>
                                                            <label>Title</label>
                                                            <SearchableSelect
                                                                options={[{ value: 'Mr', label: 'Mr' }, { value: 'Mrs', label: 'Mrs' }, { value: 'Ms', label: 'Ms' }]}
                                                                value={title}
                                                                onChange={setTitle}
                                                            />
                                                        </div>
                                                        <div className='travellerField'>
                                                            <label>First/Middle Name *</label>
                                                            <input
                                                                type='text'
                                                                required
                                                                className={errors.firstName ? 'travellerField__input--error' : ''}
                                                                value={passenger.firstName}
                                                                onChange={(e) => setPassenger({ ...passenger, firstName: e.target.value })}
                                                            />
                                                            {errors.firstName && <FieldError>First name is required</FieldError>}
                                                        </div>
                                                        <div className='travellerField'>
                                                            <label>Last Name *</label>
                                                            <input
                                                                type='text'
                                                                required
                                                                className={errors.lastName ? 'travellerField__input--error' : ''}
                                                                value={passenger.lastName}
                                                                onChange={(e) => setPassenger({ ...passenger, lastName: e.target.value })}
                                                            />
                                                            {errors.lastName && <FieldError>Last name is required</FieldError>}
                                                        </div>
                                                    </div>
                                                    <div className='travellerFieldRow'>
                                                        <div className='travellerField'>
                                                            <label>Gender *</label>
                                                            <SearchableSelect
                                                                options={GENDER_OPTIONS}
                                                                value={passenger.gender}
                                                                onChange={(gender) => setPassenger({ ...passenger, gender })}
                                                                placeholder='Select'
                                                                className={errors.gender ? 'travellerField__input--error' : ''}
                                                            />
                                                            {errors.gender && <FieldError>Gender is required</FieldError>}
                                                        </div>
                                                    </div>
                                                    <div className='travellerFieldRow'>
                                                        <div className='travellerField'>
                                                            <label>Date Of Birth (DOB) *</label>
                                                            <DatePicker
                                                                className={`travellerDateInput ${errors.dob ? 'travellerField__input--error' : ''}`}
                                                                max={maxDriverDob()}
                                                                value={passenger.dob}
                                                                onChange={(dob) => setPassenger({ ...passenger, dob })}
                                                            />
                                                            {errors.dob && (
                                                                <FieldError>{passenger.dob ? `Driver must be at least ${MIN_DRIVER_AGE} years old` : 'Date of birth is required'}</FieldError>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className='travellerFieldRow'>
                                                        <div className='travellerField'>
                                                            <label>Driving License Number{!passenger.passportNumber ? ' *' : ''}</label>
                                                            <input
                                                                type='text'
                                                                className={errors.drivingLicense ? 'travellerField__input--error' : ''}
                                                                value={passenger.drivingLicense}
                                                                onChange={(e) => setPassenger({ ...passenger, drivingLicense: e.target.value })}
                                                            />
                                                        </div>
                                                        <div className='travellerField'>
                                                            <label>Passport Number{!passenger.drivingLicense ? ' *' : ''}</label>
                                                            <input
                                                                type='text'
                                                                className={errors.passportNumber ? 'travellerField__input--error' : ''}
                                                                value={passenger.passportNumber}
                                                                onChange={(e) => setPassenger({ ...passenger, passportNumber: e.target.value })}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className='travellerFieldRow'>
                                                        <div className='travellerField'>
                                                            <label>Driving License Image</label>
                                                            <FileUploadField
                                                                file={passenger.drivingLicenseImage}
                                                                onChange={(file) => setPassenger({ ...passenger, drivingLicenseImage: file })}
                                                            />
                                                        </div>
                                                        <div className='travellerField'>
                                                            <label>Passport Image</label>
                                                            <FileUploadField
                                                                file={passenger.passportImage}
                                                                onChange={(file) => setPassenger({ ...passenger, passportImage: file })}
                                                            />
                                                        </div>
                                                    </div>
                                                    {errors.drivingLicense && <FieldError>Provide either your driving license or passport number</FieldError>}
                                                    <p className='travellerFieldNote'>Provide either your driving license or passport number.</p>
                                                    <div className='travellerFieldRow'>
                                                        <div className='travellerField'>
                                                            <label>Address *</label>
                                                            <input
                                                                type='text'
                                                                required
                                                                className={errors.address ? 'travellerField__input--error' : ''}
                                                                value={address}
                                                                onChange={(e) => setAddress(e.target.value)}
                                                            />
                                                            {errors.address && <FieldError>Address is required</FieldError>}
                                                        </div>
                                                        <div className='travellerField'>
                                                            <label>City *</label>
                                                            <input
                                                                type='text'
                                                                required
                                                                className={errors.city ? 'travellerField__input--error' : ''}
                                                                value={city}
                                                                onChange={(e) => setCity(e.target.value)}
                                                            />
                                                            {errors.city && <FieldError>City is required</FieldError>}
                                                        </div>
                                                    </div>
                                                    <div className='travellerFieldRow'>
                                                        <div className='travellerField'>
                                                            <label>State *</label>
                                                            <input
                                                                type='text'
                                                                required
                                                                className={errors.state ? 'travellerField__input--error' : ''}
                                                                value={state}
                                                                onChange={(e) => setState(e.target.value)}
                                                            />
                                                            {errors.state && <FieldError>State is required</FieldError>}
                                                        </div>
                                                        <div className='travellerField'>
                                                            <label>Country *</label>
                                                            <input
                                                                type='text'
                                                                required
                                                                className={errors.country ? 'travellerField__input--error' : ''}
                                                                value={country}
                                                                onChange={(e) => setCountry(e.target.value)}
                                                            />
                                                            {errors.country && <FieldError>Country is required</FieldError>}
                                                        </div>
                                                        <div className='travellerField'>
                                                            <label>Pincode *</label>
                                                            <input
                                                                type='text'
                                                                inputMode='numeric'
                                                                maxLength={6}
                                                                required
                                                                className={errors.pincode ? 'travellerField__input--error' : ''}
                                                                value={pincode}
                                                                onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                            />
                                                            {errors.pincode && <FieldError>Pincode is required</FieldError>}
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* <button type='button' className='addCityBtn travellerAddBtn'>Add Another Passenger</button> */}

                                                <div className='fareDetailsHead tripSecureHead mb-3 mt-4'>
                                                    <div className='bsSectionTitle'>Trip Secure</div>
                                                    {/* <img src={Icici} alt='ICICI Lombard' className='tripSecureLogo' /> */}
                                                </div>
                                                <div className='bsBox'>
                                                    {/* Static mock benefits/price — not returned by /cars/insurance. Real plan data renders below from the API. */}
                                                    {/* <div className='tripSecureBenefitsHead'>
                                                        <span className='tripSecurePlanBenefits'>PLAN BENEFITS</span>
                                                        <span className='tripSecureViewAll'>View All Benefits</span>
                                                    </div>
                                                    <div className='tripSecureBenefitsRow'>
                                                        {TRIP_SECURE_BENEFITS.map((benefit, index) => (
                                                            <div className='tripSecureBenefit' key={index}>
                                                                <img src={benefit.icon} alt="" />
                                                                <div>
                                                                    <div className='tripSecureBenefitTitle'>{benefit.title}</div>
                                                                    <div className='tripSecureBenefitSub'>{benefit.subtext}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <p className='tripSecureDisclaimer'>
                                                        Trip Secure is non-refundable. By selecting it, I confirm all travelers are Indian nationals, aged 6 months to 90 years, and accept the <button type='button' className='tripSecureLink'>T&amp;Cs</button>.
                                                    </p>
                                                    <div className='tripSecurePrice'>₹ 499 <span>Per Person</span></div>
                                                    <label className='tripSecureRadioRow' onClick={() => setTripSecureChoice('yes')}>
                                                        <span className={`fareTypeRadio ${tripSecureChoice === 'yes' ? 'checked' : ''}`} />
                                                        Yes, Secure my trip.
                                                    </label>
                                                    <label className='tripSecureRadioRow' onClick={() => setTripSecureChoice('no')}>
                                                        <span className={`fareTypeRadio ${tripSecureChoice === 'no' ? 'checked' : ''}`} />
                                                        No, I will book without trip secure.
                                                    </label> */}

                                                    {insuranceLoading && <CarSearchLoader size='compact' text='Loading insurance details...' />}
                                                    {!insuranceLoading && !insuranceLoaded && (!passenger.firstName || !passenger.lastName) && (
                                                        <p className='travellerFieldNote'>Enter the passenger's first and last name above to load Trip Secure plans automatically.</p>
                                                    )}
                                                    {!insuranceLoading && insuranceLoaded && !insurancePlans.length && (
                                                        <p className='travellerFieldNote mt-2'>No insurance plans available right now.</p>
                                                    )}
                                                    {!insuranceLoading && insurancePlans.map((plan) => (
                                                        <div key={plan.plan_id}>
                                                            {/* <div className='bsFareDivider mt-3' /> */}
                                                            <div className='bsFareRow'>
                                                                <span>{plan.plan_name} <span className='travellerFieldNote'>({plan.provider_company})</span></span>
                                                                <span>{formatMoney(plan.plan_cost_amount, plan.plan_cost_currency)}</span>
                                                            </div>
                                                            {plan.quote_detail_url && (
                                                                <a className='commonGredientText carDetail__infoLink' href={plan.quote_detail_url} target='_blank' rel='noreferrer'>
                                                                    {plan.details_to_display?.title || 'View Plan Terms'}
                                                                </a>
                                                            )}
                                                            {plan.details_to_display?.description?.length > 0 && (
                                                                <ul className='carDetailChecklist mt-2'>
                                                                    {plan.details_to_display.description.map((line, lineIndex) => (
                                                                        <li key={lineIndex}><img src={GreenTick} alt="" /> {line}</li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                            {plan.details_to_display?.more_informations && Object.entries(plan.details_to_display.more_informations).map(([heading, points], sectionIndex) => (
                                                                <div key={sectionIndex} className='mt-3'>
                                                                    <div className='bsSectionTitle fareBaggageTitle' dangerouslySetInnerHTML={{ __html: heading }} />
                                                                    <ul className='carDetailChecklist'>
                                                                        {points.map((point, pointIndex) => (
                                                                            <li key={pointIndex}><img src={GreenTick} alt="" /> {point}</li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            ))}
                                                            {selectedInsurance?.plan_id === plan.plan_id ? (
                                                                <div className='d-flex gap-2 mt-4'>
                                                                    <button type='button' className='commonBtn w-100' disabled>Selected</button>
                                                                    <button type='button' className='travellerAddBtn w-100 my-0' style={{ height: 52 }} onClick={() => dispatch(setSelectedInsurance(null))}>Cancel</button>
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    type='button'
                                                                    className='travellerAddBtn w-100 mt-3'
                                                                    onClick={() => dispatch(setSelectedInsurance(plan))}
                                                                >
                                                                    Add This Cover
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className='bsSectionTitle mt-3'>Important Information</div>
                                                <div className='bsBox'>
                                                    <ul className='bsInfoList'>
                                                        {TRAVELLER_INFO.map((info, index) => (
                                                            <li key={index}>{info}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/*
                                    Renter Details — commented out for now (duplicates Contact/Passenger Details above; revisit later).
                                    <div className='renterDetil fairDetails'>
                                        <div className='fareDetailsHead' onClick={() => setRenterOpen(!renterOpen)}>
                                            <div className='bsSectionTitle'>Enter Renter Details</div>
                                            <span className={`fareCollapseIcon ${renterOpen ? 'open' : ''}`}>&#8963;</span>
                                        </div>

                                        {renterOpen && (
                                            <div className='fareDetailsBody'>
                                                <div className='travellerFieldRow'>
                                                    <div className='travellerField'>
                                                        <label>Email Address</label>
                                                        <input type='email' defaultValue={contact.email} disabled />
                                                    </div>
                                                    <div className='travellerField'>
                                                        <label>Mobile Number</label>
                                                        <input type='tel' defaultValue={contact.mobile} disabled />
                                                    </div>
                                                </div>
                                                <p className='travellerFieldNote'>Same as contact details above</p>

                                                <div className='travellerFieldRow mt-3'>
                                                    <div className='travellerField'>
                                                        <label>First/Middle Name</label>
                                                        <input type='text' defaultValue={passenger.firstName} disabled />
                                                    </div>
                                                    <div className='travellerField'>
                                                        <label>Last Name</label>
                                                        <input type='text' defaultValue={passenger.lastName} disabled />
                                                    </div>
                                                </div>

                                                <div className='travellerFieldRow'>
                                                    <div className='travellerField'>
                                                        <label>DOB</label>
                                                        <div className='renterDobRow'>
                                                            <select defaultValue=''>
                                                                <option value='' disabled>DD</option>
                                                                {RENTER_DAYS.map((day) => <option key={day}>{day}</option>)}
                                                            </select>
                                                            <select defaultValue=''>
                                                                <option value='' disabled>MM</option>
                                                                {RENTER_MONTHS.map((month) => <option key={month}>{month}</option>)}
                                                            </select>
                                                            <select defaultValue=''>
                                                                <option value='' disabled>YYYY</option>
                                                                {RENTER_YEARS.map((year) => <option key={year}>{year}</option>)}
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className='travellerField'>
                                                        <label>Country of Residence</label>
                                                        <select value={country} onChange={(e) => setCountry(e.target.value)}>
                                                            {RENTER_COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className='travellerFieldRow'>
                                                    <div className='travellerField'>
                                                        <label>Driving License Number</label>
                                                        <input type='text' defaultValue={renter.drivingLicense} disabled />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    */}

                                    {/* <div className='travellingPlane fairDetails'>
                                        <div className='fareDetailsHead' onClick={() => setPlaneOpen(!planeOpen)}>
                                            <div className='bsSectionTitle'>Travelling by plane? Add your flight number</div>
                                            <span className={`fareCollapseIcon ${planeOpen ? 'open' : ''}`}>&#8963;</span>
                                        </div>

                                        {planeOpen && (
                                            <div className='fareDetailsBody'>
                                                <p className='bookingConfirmSentText mb-3'>Let your car rental supplier know when to expect you. You can add it later</p>
                                                <div className='travellerFieldRow'>
                                                    <div className='travellerField'>
                                                        <label>Flight Number (Optional, e.g. AI 1617)</label>
                                                        <input type='text' value={flightNumber} onChange={(e) => setFlightNumber(e.target.value)} />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div> */}

                                    {/* <div className='fairDetails'>
                                        <div className='fareDetailsHead' onClick={() => {
                                            const next = !coverageOpen
                                            setCoverageOpen(next)
                                            if (next && !insuranceLoaded) loadInsurance()
                                        }}>
                                            <div className='bsSectionTitle'>Upgrade to Full Coverage and Relax</div>
                                            <span className={`fareCollapseIcon ${coverageOpen ? 'open' : ''}`}>&#8963;</span>
                                        </div>

                                        {coverageOpen && (
                                            <div className='fareDetailsBody'>
                                                {insuranceLoading && <CarSearchLoader size='compact' text='Loading coverage options...' />}
                                                {!insuranceLoading && insuranceLoaded && !insurancePlans.length && (
                                                    <p>No coverage plans available right now.</p>
                                                )}
                                                {!insuranceLoading && !insuranceLoaded && (
                                                    <button type='button' className='travellerAddBtn' onClick={loadInsurance}>Load Coverage Options</button>
                                                )}
                                                {!insuranceLoading && insurancePlans.length > 0 && (
                                                    <div className='carDetailGrid'>
                                                        {insurancePlans.map((plan) => (
                                                            <div className='bgWhite commonBorder borderRadius20 paddingCommon20 carDetail__extraCard' key={plan.plan_id}>
                                                                <div className='carCoverageCard__head'>
                                                                    <div>
                                                                        <div className='carDetail__extraTitle'>{plan.plan_name}</div>
                                                                        <div className='carDetail__extraDesc'>{plan.provider_company}</div>
                                                                        {plan.quote_detail_url && (
                                                                            <a className='commonGredientText carDetail__infoLink' href={plan.quote_detail_url} target='_blank' rel='noreferrer'>Full Cover Explained</a>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div className='carCoverageCard__price'>{formatMoney(plan.plan_cost_amount, plan.plan_cost_currency)}</div>
                                                                <div className='carCoverageCard__priceNote'>for the rental period</div>

                                                                <button
                                                                    type='button'
                                                                    className={selectedInsurance?.plan_id === plan.plan_id ? 'commonBtn w-100' : 'travellerAddBtn'}
                                                                    onClick={() => dispatch(setSelectedInsurance(selectedInsurance?.plan_id === plan.plan_id ? null : plan))}
                                                                >
                                                                    {selectedInsurance?.plan_id === plan.plan_id ? 'Selected' : 'Book With Coverage'}
                                                                </button>

                                                                {plan.details_to_display?.description?.length > 0 && (
                                                                    <>
                                                                        <div className='bsSectionTitle mt-3'>{plan.details_to_display.title || "What's covered?"}</div>
                                                                        <ul className='carDetailChecklist'>
                                                                            {plan.details_to_display.description.map((line, itemIndex) => (
                                                                                <li key={itemIndex}><img src={GreenTick} alt="" /> {line}</li>
                                                                            ))}
                                                                        </ul>
                                                                    </>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div> */}

                                    {/* Payment Details (card fields) now live on the next page — carPayment.jsx — which calls /cars/book. */}

                                    <div className='totalPay bgWhite paddingCommon20 borderRadius20 commonBorder'>
                                        <button type='button' className='commonBtn w-100' onClick={handleContinueToPayment}>
                                            {`Continue to Payment ${totalToPayLabel}`}
                                        </button>
                                        <p className='bsFareNote totalPayNote'>By proceeding, you agree to our Terms &amp; Conditions</p>
                                    </div>
                                </div>
                                <div className='bookingSidebar'>
                                    <div className='bsSection'>
                                        <div className='bsSectionTitle'>Important Information</div>
                                        <div className='bsBox'>
                                            <ul className='bsInfoList'>
                                                {IMPORTANT_INFO.map((info, index) => (
                                                    <li key={index}>{info}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>

                                    <div className='bsSection'>
                                        <div className='bsSectionTitle'>Fare Breakup</div>
                                        <div className='bsBox'>
                                            <div className='bsFareRow'>
                                                <span>Vehicle Charge</span>
                                                <span>{formatMoney(bookingContext.fare_summary?.vehicle_charge, bookingContext.currency)}</span>
                                            </div>
                                            <div className='bsFareRow'>
                                                <span>Coverage Charge</span>
                                                <span>{formatMoney(bookingContext.fare_summary?.coverage_charge, bookingContext.currency)}</span>
                                            </div>
                                            <div className='bsFareRow'>
                                                <span>Optional Extras</span>
                                                <span>{formatMoney(bookingContext.fare_summary?.optional_extra_charge, bookingContext.currency)}</span>
                                            </div>
                                            {selectedInsurance && (
                                                <div className='bsFareRow'>
                                                    <span>Insurance ({selectedInsurance.plan_name})</span>
                                                    <span>{formatMoney(selectedInsurance.plan_cost_amount, selectedInsurance.plan_cost_currency)}</span>
                                                </div>
                                            )}
                                            <div className='bsFareDivider' />
                                            <div className='bsFareRow bsFareRow--total'>
                                                <span>TOTAL AMOUNT</span>
                                                <span>{totalToPayLabel}</span>
                                            </div>
                                            <p className='bsFareNote'>Convenience fee is charged for payment processing and platform services.</p>
                                        </div>
                                    </div>

                                    {/* No coupon/discount endpoint exists for cars — this was static placeholder data. */}
                                    {/* <div className='bsSection'>
                                        <div className='bsSectionTitle'>Offers &amp; Discounts</div>
                                        <div className='bsBox'>
                                            <input type='text' className='bsCouponInput' placeholder='Enter coupon code' />
                                            {COUPONS.map((coupon, index) => (
                                                <div className='bsCouponCard' key={index}>
                                                    <div className='bsCouponHead'>
                                                        <span className='bsCouponCode'>{coupon.code}</span>
                                                        <span className='bsCouponOff'>{coupon.off}</span>
                                                    </div>
                                                    <p className='bsCouponDesc'>{coupon.desc}</p>
                                                    <button type='button' className='bsCouponApply'>APPLY COUPON</button>
                                                </div>
                                            ))}
                                        </div>
                                    </div> */}
                                </div>
                            </div>
                        </div>
                    </div>


                </div>
            </section>
        </>
    )
}

export default CarBookingDetail
